import { CoreLogService } from "../log.service";
import { Observable, Subject } from "rxjs";
import { Client } from "./comEnvironmentClient";
import { environment } from "../../../../environments/environment";
import { YourAPPProductType } from "../../../../app/core/shared/contentTypes";


export interface IFCMNotificationData {
  wasTapped: boolean;

  eventInformation?: {
    contentID: string,
    contentType: string
  }

  notification?: {
    title?: string;
    body?: string;
    sound?: string;
    icon?: string;
  };
}

export interface IFirebaseCloudMessaging {
  /**
   * Request permission from user that notifactions are desired
   */
  requestPermission(): Promise<boolean>;

  /**
   * Gets device's current registration id.
   * Keep in mind, the function will return null if the token has not been established yet.
   */
  getToken(): Promise<string | null>;

  /**
   * Callback firing when receiving a new Firebase token. It serves as a shortcut to listen to eventTarget's "tokenRefresh" event.
   */
  onTokenRefresh(): Observable<string>;

  /**
   * Callback firing when receiving new notifications. It serves as a shortcut to listen to eventTarget's "notification" event.
   */
  onNotification(): Observable<IFCMNotificationData>;
}

export class FirebaseMessaging implements IFirebaseCloudMessaging {
  private readonly firebase: any;
  private readonly diplayEveryNotification;
  private hasPermission: boolean;

  constructor(private logger: CoreLogService, private client: Client) {
    if (client.isMobile) {
      this.firebase = cordova.plugins["firebase"];
    }

    this.diplayEveryNotification = true;
    this.hasPermission = false;
  }

  async requestPermission(): Promise<boolean> {
    if (!this.isNotifiable()) {
      this.hasPermission = false;
    } else {
      try {
        await this.firebase.messaging.requestPermission({ forceShow: this.diplayEveryNotification });
        this.hasPermission = true;
      } catch (error) {
        this.hasPermission = false;
      }
    }
    return this.hasPermission;
  }

  async getToken(): Promise<string> {
    if (!this.hasPermission) {
      return "";
    }

    try {
      return await this.firebase.messaging.getToken();
    } catch (error) {
      this.logFcmError("getToken", error);
      return "";
    }
  }

  onTokenRefresh(): Observable<string> {
    const result = new Subject<string>();

    this.firebase.messaging.onTokenRefresh(function () {
      result.next(this.getToken());
    });

    return result.asObservable();
  }

  onNotification(): Observable<IFCMNotificationData> {
    const result = new Subject<IFCMNotificationData>();

    let foregroundSuccessCallback = function (notification: any) {
      const fcmData: IFCMNotificationData = { wasTapped: false };

      if (environment.producttype === YourAPPProductType.app_android) {
        fcmData.notification = {
          title: notification.gcm.title,
          body: notification.gcm.body,
          icon: notification.gcm.imageUrl,
        };
      }
      // Else IOS does not need to create a notification! I Think Android will be the same.
      // TODO Remove local notification usage.

      if (notification.contentID !== undefined && notification.contentType !== undefined) {
        fcmData.eventInformation = { contentID: notification.contentID, contentType: notification.contentType };
      }

      result.next(fcmData);
    };

    let backgroundSuccessCallback = function (notification: any) {
      const fcmData: IFCMNotificationData = { wasTapped: true };

      if (notification.contentID !== undefined && notification.contentType !== undefined) {
        fcmData.eventInformation = { contentID: notification.contentID, contentType: notification.contentType };
      }

      result.next(fcmData);
    };

    this.firebase.messaging.onMessage(foregroundSuccessCallback, console.error);
    this.firebase.messaging.onBackgroundMessage(backgroundSuccessCallback, console.error);
    return result.asObservable();
  }

  private isNotifiable(): boolean {
    return this.firebase !== undefined && this.client.isMobile;
  }

  private logFcmError(usedMethod: string, exception?: any): void {
    this.logger.error("FirebaseCloudMessaging", `cordova.plugins.firebase.messaging.${usedMethod} ist fehlgeschlagen!`, exception);
  }
}
